import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { debounceTime, filter, map, tap, withLatestFrom } from 'rxjs/operators';
import { WebSocketService } from '../services/web-socket.service';

import { WebSocketActions } from '../redux/websocket.actions';
import {
  selectConnectionId,
  selectIsConnected,
  selectWorkspaceId,
} from './websocket.selectors';
import { WebSocketState } from '../models/message';

@Injectable()
export class WebSocketEffects {
  private readonly _actions$ = inject(Actions);
  private readonly _webSocketService = inject(WebSocketService);
  private readonly _store = inject(Store<WebSocketState>);
  private connecting = false;

  /**
   * Effect to connect to webSocket
   */

  connectWebSocket$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(WebSocketActions.connectWebSocket),
        debounceTime(100),
        withLatestFrom(
          this._store.pipe(select(selectIsConnected)),
          this._store.pipe(select(selectConnectionId)),
          this._store.pipe(select(selectWorkspaceId)),
        ),
        filter(([_action, isConnected]) => !isConnected && !this.connecting),
        tap(() => {
          this.connecting = true;
        }),
        map(([_action, _isConnected, connectionId, _workspaceId]) => {
          this._webSocketService
            .connect(connectionId ?? undefined)
            .finally(() => {
              this.connecting = false;
            });
        }),
      ),
    { dispatch: false },
  );

  /**
   * Effect to disconnect from websocket
   */
  disconnectWebSocket$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(WebSocketActions.disconnectWebSocket),
        tap(() => {
          this.connecting = false;
        }),
        map(() => {
          console.log('disconnecting from websocket');
          this._webSocketService.disconnect();
        }),
      ),
    { dispatch: false },
  );

  subscribeUserNotifications$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(WebSocketActions.subscribeUserNotification),
        tap(({ userId }) => {
          this._webSocketService.subscribeUserNotifications(userId);
        }),
      ),
    { dispatch: false },
  );

  unSubscribeUserNotifications$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(WebSocketActions.unSubscribeUserNotification),
        tap(({ userId }) => {
          this._webSocketService.unsubscribeUserNotifications(userId);
        }),
      ),
    { dispatch: false },
  );
}
